import {
    AnyAction,
    createListenerMiddleware,
    isAllOf,
    isAnyOf,
    PayloadAction,
    Action,
    isFulfilled
} from '@reduxjs/toolkit';
import { isRTKQMeta, RTKQMeta, ToastMessage } from '@workday/upc-common';
import { accountApiSlice } from '../slices/account-api.slice';
import { authApiSlice } from '../slices/auth-api.slice';
import { toastActions, WclToastData } from '../slices/toast.slice';
import { TypeGuard } from '@reduxjs/toolkit/dist/tsHelpers';
import { RootState, toastSelectors } from '../store';
import { DEFAULT_ERROR, isServerErrorAction } from '../utils/server-error';
import { ServerErrorAction } from '../slices/types';
import { ErrorCode } from '../../i18n';

type ToastConfig = Partial<Omit<ToastMessage<Partial<WclToastData>>, 'id'>>;

export interface ToastMatcherConfig<T = any> {
    matcher: TypeGuard<T>;
    toastConfig?: ToastConfig;
}

type ConfigurableToast = RTKQMeta<{
    showToast?: boolean;
}>;

const is500Error = (action: AnyAction): action is PayloadAction<any> => {
    return isRTKQMeta(action) && action.meta.baseQueryMeta.response?.status === 500;
};

const isResetPasswordErrorToast = (action: AnyAction): action is PayloadAction<any> => {
    return authApiSlice.endpoints.resetPasswordInit.matchRejected(action) && action.payload?.status !== 429;
};

const isConfigurableToast = (action: AnyAction): action is Action<ConfigurableToast> =>
    action.meta?.arg?.originalArgs?.showToast;

const createServerErrorMatcher =
    ({ errorCode }: { errorCode: ErrorCode }) =>
    (action: AnyAction): action is ServerErrorAction => {
        if (!isServerErrorAction(action)) {
            return false;
        }

        return action.payload.data.errorCode === errorCode;
    };

/**
 *
 * This configuration is for API-driven toast messages. It may work for your use-case.
 *
 * It is good for success messages and errors where there are no conflicting inline errors.
 *
 * If multiple components use the same API then beware - you might not want a toast message in all locations.
 *
 * Inline errors (errors on forms) should ideally be handled in the component presenting them. If this is your
 * use-case then take a look at the useErrorHandling hook. Example usage in LoginPasswordEntry.
 *
 */
const matcherConfigs: Array<ToastMatcherConfig> = [
    {
        matcher: isAnyOf(
            accountApiSlice.endpoints.mfaInit.matchFulfilled,
            isAllOf(
                isConfigurableToast,
                isAnyOf(
                    authApiSlice.endpoints.enrollmentEmailInit.matchFulfilled,
                    accountApiSlice.endpoints.addAlias.matchFulfilled
                )
            )
        ),
        toastConfig: {
            data: {
                translationKey: 'verificationEmailSentSuccess'
            }
        }
    },
    {
        matcher: isAllOf(
            isConfigurableToast,
            isAnyOf(
                authApiSlice.endpoints.enrollmentPhoneInit.matchFulfilled,
                accountApiSlice.endpoints.initUpdatePhone.matchFulfilled
            )
        ),
        toastConfig: {
            data: {
                translationKey: 'otpSmsSentSuccess'
            }
        }
    },
    {
        matcher: accountApiSlice.endpoints.changePassword.matchFulfilled,
        toastConfig: {
            data: {
                translationKey: 'changePasswordSuccess'
            }
        }
    },
    {
        matcher: isAnyOf(
            accountApiSlice.endpoints.updateAuthenticator.matchFulfilled,
            authApiSlice.endpoints.verifyNewAuthenticator.matchFulfilled
        ),
        toastConfig: {
            data: {
                translationKey: 'newAuthenticatorSuccess'
            }
        }
    },
    {
        matcher: isAnyOf(
            authApiSlice.endpoints.resendPhoneResetPassword.matchFulfilled,
            authApiSlice.endpoints.resendEmailResetPassword.matchFulfilled
        ),
        toastConfig: {
            data: {
                translationKey: 'sentAnotherCodeSuccess'
            }
        }
    },
    {
        matcher: authApiSlice.endpoints.initEmailLogin.matchRejected,
        toastConfig: {
            data: {
                translationKey: 'problemLoggingInError'
            }
        }
    },
    {
        matcher: accountApiSlice.endpoints.gdprDeleteUser.matchFulfilled,
        toastConfig: {
            data: {
                translationKey: 'deleteAccountSuccess'
            }
        }
    },
    {
        matcher: authApiSlice.endpoints.resetPassword.matchFulfilled,
        toastConfig: {
            data: {
                translationKey: 'resetPasswordSuccess'
            }
        }
    },
    {
        matcher: authApiSlice.endpoints.getInvitation.matchRejected,
        toastConfig: {
            data: {
                translationKey: 'cannotFindInviteError'
            }
        }
    },
    {
        matcher: accountApiSlice.endpoints.uploadProfileImage.matchFulfilled,
        toastConfig: {
            data: {
                translationKey: 'imageUpdateSuccess'
            }
        }
    },
    {
        matcher: accountApiSlice.endpoints.updatePhoneVerifyOtp.matchFulfilled,
        toastConfig: {
            data: {
                translationKey: 'changePhoneNumberSuccess'
            }
        }
    },
    {
        matcher: accountApiSlice.endpoints.verifyAuthenticatorFromSettings.matchFulfilled,
        toastConfig: {
            data: {
                translationKey: 'verifyAuthenticatorFromSettingsSuccess'
            }
        }
    },
    {
        matcher: isAnyOf(
            createServerErrorMatcher({ errorCode: 'UPC-1052' }),
            createServerErrorMatcher({ errorCode: 'UPC-1053' }),
            accountApiSlice.endpoints.deleteAlias.matchRejected,
            accountApiSlice.endpoints.addAliasAsPrimary.matchRejected,
            authApiSlice.endpoints.enrollmentInvitation.matchRejected,
            authApiSlice.endpoints.enrollmentPasswordSubmit.matchRejected,
            authApiSlice.endpoints.resetPassword.matchRejected,
            authApiSlice.endpoints.enrollmentEmailInit.matchRejected,
            authApiSlice.endpoints.enrollmentPhoneInit.matchRejected,
            authApiSlice.endpoints.resetPasswordUserConsentForPhoneOtp.matchRejected,
            isResetPasswordErrorToast,
            authApiSlice.endpoints.resendPhoneResetPassword.matchRejected,
            authApiSlice.endpoints.resendEmailResetPassword.matchRejected,
            accountApiSlice.endpoints.changePassword.matchRejected,
            accountApiSlice.endpoints.gdprDeleteUser.matchRejected
        )
    },
    {
        matcher: is500Error,
        toastConfig: {
            data: {
                translationKey: '500Error'
            }
        }
    }
];

const matchers = matcherConfigs.map(mc => mc.matcher);

function getToastData(action: PayloadAction<unknown>, toastConfig?: ToastConfig): WclToastData {
    // defaults
    const wclToastData: WclToastData = {
        type: 'error',
        text: DEFAULT_ERROR,
        translationKey: 'UnknownError'
    };

    if (isServerErrorAction(action)) {
        wclToastData.type = 'error';
        wclToastData.text = action.payload.data.error || DEFAULT_ERROR;
        wclToastData.translationKey = action.payload.data.errorCode || 'UnknownError';
    } else if (isFulfilled(action)) {
        wclToastData.type = 'success';
        wclToastData.text = toastConfig?.data?.text || 'Success!';
        wclToastData.translationKey = toastConfig?.data?.translationKey || 'UnknownSuccess';
    }

    return wclToastData;
}

const middleware = createListenerMiddleware();

middleware.startListening({
    predicate: isAnyOf(...matchers),
    effect: (action: PayloadAction<unknown>, api) => {
        const found = matcherConfigs.find(mc => mc.matcher(action));
        if (found) {
            const toastConfig = found.toastConfig;

            api.dispatch(
                toastActions.showMessage({
                    data: getToastData(action, toastConfig)
                })
            );
        }
    }
});

middleware.startListening({
    actionCreator: toastActions.showMessage,
    // runs when we get a showMessage action and a toast is being shown
    effect: async (action: PayloadAction<ToastMessage<unknown>>, api) => {
        const messages = toastSelectors.selectAll(api.getState() as RootState);

        if (messages.length === 4) {
            // clear the last message
            const found = messages[messages.length - 1];
            api.dispatch(toastActions.clearMessage(found.id));
        }

        // wait for a clearMessage action matching this toast
        const toastIsCleared = await api.take(
            takeAction => toastActions.clearMessage.match(takeAction) && takeAction.payload === action.payload.id,
            action.payload.timeInMillis
        );

        // if none arrives, clear the toast after the timeout
        if (!toastIsCleared) {
            api.dispatch(toastActions.clearMessage(action.payload.id));
        }
    }
});

export const toastMiddleware = middleware;
